import { useState } from "react";
import { motion } from "framer-motion";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
    Loader2,
    CheckCircle2,
    ArrowRight,
    Truck,
    Boxes,
    Sparkles,
    Users,
    Hammer,
} from "lucide-react";
import { api } from "@/lib/api";
import { Reveal, Stagger, Item } from "@/components/site/Reveal";
import { IconSticker } from "@/components/site/Sticker";
import CartoonSticker from "@/components/site/CartoonStickers";

const VENDOR_TYPES = [
    "Movers",
    "Packers",
    "Cleaners",
    "Furniture assembly",
    "Handyman",
    "Junk removal",
    "Storage",
    "Local helpers",
];

const TILES = [
    { icon: Truck, label: "Movers" },
    { icon: Boxes, label: "Packers" },
    { icon: Sparkles, label: "Cleaners" },
    { icon: Hammer, label: "Furniture assembly" },
    { icon: Users, label: "Helpers" },
];

const LOOKING_FOR = [
    { title: "Reliability", body: "Show up on time, every time." },
    { title: "Clear communication", body: "Updates throughout the job." },
    { title: "Transparent pricing", body: "Clean quotes — no surprise add-ons." },
];

export default function Vendors() {
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);
    const [form, setForm] = useState({
        full_name: "",
        business_name: "",
        email: "",
        phone: "",
        service_type: "",
        service_area: "",
        years_experience: "",
        rates: "",
        availability: "",
        insurance_status: "",
        references: "",
        message: "",
    });

    const update = (field, val) => setForm((p) => ({ ...p, [field]: val }));

    async function submit(e) {
        e.preventDefault();
        if (!form.full_name || !form.email || !form.phone || !form.service_type) {
            toast.error("Please fill in name, email, phone, and service type.");
            return;
        }
        setSubmitting(true);
        try {
            await api.post("/vendors", form);
            setDone(true);
            toast.success("Application received. We'll be in touch.");
        } catch (err) {
            const msg = err?.response?.data?.detail || err.message;
            toast.error("Could not submit application: " + msg);
        } finally {
            setSubmitting(false);
        }
    }

    if (done) {
        return (
            <div className="section" data-testid="vendor-success">
                <div className="container-atm">
                    <Reveal>
                        <div className="card-3d mx-auto max-w-2xl p-10 text-center">
                            <span className="mx-auto inline-flex rounded-full bg-emerald-50 p-3 text-emerald-600">
                                <CheckCircle2 size={32} />
                            </span>
                            <h1 className="mt-6 text-3xl font-semibold">
                                Thanks — your application has been received.
                            </h1>
                            <p className="mt-4 text-[15px] text-[#475569]">
                                We'll review your details and reach out if there's a fit.
                            </p>
                        </div>
                    </Reveal>
                </div>
            </div>
        );
    }

    return (
        <div data-testid="vendors-page">
            <section className="section relative gradient-warm overflow-hidden">
                <CartoonSticker name="mover" width={100} rotate={-6} className="hidden xl:inline-flex top-12 right-6" />
                <div className="container-atm relative">
                    <Reveal>
                        <span className="eyebrow">Task Partners</span>
                        <h1 className="mt-4 max-w-3xl text-4xl font-semibold leading-[1.05] sm:text-5xl lg:text-6xl">
                            Work with AllTask Moves as a{" "}
                            <span className="bg-gradient-to-r from-[#F26B1F] to-[#FF8E47] bg-clip-text text-transparent">
                                Task Partner.
                            </span>
                        </h1>
                    </Reveal>
                    <Reveal delay={0.1}>
                        <p className="mt-6 max-w-2xl text-base text-[#475569] md:text-lg">
                            We're building a reliable roster of <strong>movers, packers,
                            cleaners, furniture-assembly providers, handyman services,
                            junk removal providers, storage providers, and local helpers</strong>{" "}
                            for Toronto and GTA moves.
                        </p>
                    </Reveal>

                    <Stagger className="mt-10 flex flex-wrap gap-3">
                        {TILES.map((t) => (
                            <Item key={t.label}>
                                <span className="inline-flex items-center gap-2 rounded-full border border-[#ECE4D3] bg-white/80 px-4 py-2 text-sm font-semibold text-[#0E2A47] backdrop-blur">
                                    <span className="rounded-full bg-[#FDF1E7] p-1.5 text-[#F26B1F]">
                                        <t.icon size={14} strokeWidth={2} />
                                    </span>
                                    {t.label}
                                </span>
                            </Item>
                        ))}
                    </Stagger>
                </div>
            </section>

            <section className="section relative">
                <CartoonSticker name="lamp" width={76} rotate={6} className="hidden xl:inline-flex top-8 left-4" />

                <div className="container-atm relative grid gap-10 lg:grid-cols-12">
                    <aside className="lg:col-span-5">
                        <Reveal>
                            <span className="eyebrow">What we look for</span>
                            <h2 className="mt-3 text-3xl font-semibold leading-tight sm:text-4xl">
                                Reliable partners.{" "}
                                <span className="bg-gradient-to-r from-[#F26B1F] to-[#FF8E47] bg-clip-text text-transparent">
                                    Respectful service.
                                </span>
                            </h2>
                        </Reveal>
                        <Stagger className="mt-8 space-y-4">
                            {LOOKING_FOR.map((l) => (
                                <Item key={l.title}>
                                    <motion.div
                                        whileHover={{ x: 4 }}
                                        transition={{ duration: 0.3 }}
                                        className="card-3d flex items-start gap-4 p-6"
                                    >
                                        <span className="rounded-xl bg-[#FDF1E7] p-2 text-[#F26B1F]">
                                            <Sparkles size={20} strokeWidth={1.6} />
                                        </span>
                                        <div>
                                            <h3 className="text-base font-semibold">
                                                {l.title}
                                            </h3>
                                            <p className="mt-1 text-sm text-[#475569]">
                                                {l.body}
                                            </p>
                                        </div>
                                    </motion.div>
                                </Item>
                            ))}
                        </Stagger>
                    </aside>

                    <Reveal delay={0.1} className="lg:col-span-7">
                        <form
                            onSubmit={submit}
                            className="card-3d p-7 md:p-10"
                            data-testid="vendor-form"
                        >
                            <h3 className="text-xl font-semibold">
                                Apply to join the roster
                            </h3>
                            <p className="mt-1 text-sm text-[#475569]">
                                Tell us about your business and we'll be in touch.
                            </p>

                            <div className="mt-6 grid gap-5 md:grid-cols-2">
                                <Field id="v-full" label="Full name" required>
                                    <Input
                                        id="v-full"
                                        value={form.full_name}
                                        onChange={(e) => update("full_name", e.target.value)}
                                        data-testid="vendor-full-name"
                                    />
                                </Field>
                                <Field id="v-biz" label="Business name">
                                    <Input
                                        id="v-biz"
                                        value={form.business_name}
                                        onChange={(e) => update("business_name", e.target.value)}
                                        data-testid="vendor-business-name"
                                    />
                                </Field>
                                <Field id="v-email" label="Email" required>
                                    <Input
                                        id="v-email"
                                        type="email"
                                        value={form.email}
                                        onChange={(e) => update("email", e.target.value)}
                                        data-testid="vendor-email"
                                    />
                                </Field>
                                <Field id="v-phone" label="Phone" required>
                                    <Input
                                        id="v-phone"
                                        value={form.phone}
                                        onChange={(e) => update("phone", e.target.value)}
                                        data-testid="vendor-phone"
                                    />
                                </Field>
                                <Field id="v-type" label="Service type" required>
                                    <Select
                                        value={form.service_type}
                                        onValueChange={(v) => update("service_type", v)}
                                    >
                                        <SelectTrigger
                                            id="v-type"
                                            className="h-12 rounded-xl"
                                            data-testid="vendor-service-type"
                                        >
                                            <SelectValue placeholder="Select" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {VENDOR_TYPES.map((t) => (
                                                <SelectItem key={t} value={t}>
                                                    {t}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </Field>
                                <Field id="v-area" label="Service area">
                                    <Input
                                        id="v-area"
                                        value={form.service_area}
                                        onChange={(e) => update("service_area", e.target.value)}
                                        placeholder="Downtown Toronto, North York"
                                        data-testid="vendor-service-area"
                                    />
                                </Field>
                                <Field id="v-yrs" label="Years of experience">
                                    <Input
                                        id="v-yrs"
                                        value={form.years_experience}
                                        onChange={(e) => update("years_experience", e.target.value)}
                                        data-testid="vendor-years"
                                    />
                                </Field>
                                <Field id="v-rates" label="Rates">
                                    <Input
                                        id="v-rates"
                                        value={form.rates}
                                        onChange={(e) => update("rates", e.target.value)}
                                        placeholder="$80/hr / $300 flat"
                                        data-testid="vendor-rates"
                                    />
                                </Field>
                                <Field id="v-avail" label="Availability">
                                    <Input
                                        id="v-avail"
                                        value={form.availability}
                                        onChange={(e) => update("availability", e.target.value)}
                                        data-testid="vendor-availability"
                                    />
                                </Field>
                                <Field id="v-ins" label="Insurance status">
                                    <Input
                                        id="v-ins"
                                        value={form.insurance_status}
                                        onChange={(e) => update("insurance_status", e.target.value)}
                                        data-testid="vendor-insurance"
                                    />
                                </Field>
                            </div>

                            <div className="mt-5">
                                <Field id="v-refs" label="References">
                                    <Textarea
                                        id="v-refs"
                                        rows={2}
                                        value={form.references}
                                        onChange={(e) => update("references", e.target.value)}
                                        data-testid="vendor-references"
                                    />
                                </Field>
                            </div>
                            <div className="mt-4">
                                <Field id="v-msg" label="Message">
                                    <Textarea
                                        id="v-msg"
                                        rows={3}
                                        value={form.message}
                                        onChange={(e) => update("message", e.target.value)}
                                        data-testid="vendor-message"
                                    />
                                </Field>
                            </div>

                            <Button
                                type="submit"
                                disabled={submitting}
                                className="btn-primary mt-7 w-full !py-4"
                                data-testid="vendor-submit-btn"
                            >
                                {submitting ? (
                                    <Loader2 className="animate-spin" size={18} />
                                ) : (
                                    <>
                                        Submit Application <ArrowRight size={18} />
                                    </>
                                )}
                            </Button>
                        </form>
                    </Reveal>
                </div>
            </section>
        </div>
    );
}

function Field({ id, label, required, children }) {
    return (
        <div>
            <Label htmlFor={id} className="text-sm font-medium text-[#0E2A47]">
                {label}{" "}
                {required && <span className="text-[#F26B1F]">*</span>}
            </Label>
            <div className="mt-2">{children}</div>
        </div>
    );
}
